import {
  Button,
  PanelError,
  Select,
  SkeletonStack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@scribenote/brian-design-system";
import { useMemo, useState } from "react";

import {
  CATALOG_CATEGORIES,
  catalogMatch,
  counterpart,
  entryVersion,
  fullLabel,
  indexCatalog,
  labelContext,
  scopeVersionText,
  type CatalogCategory,
  type CatalogEntry,
  type CatalogIndex,
  type CatalogMatch,
} from "./catalogGraph";
import { CatalogInspector } from "./CatalogInspector";
import { CatalogTable } from "./CatalogTable";
import { RelationshipMap } from "./RelationshipMap";
import { ALL_POLICY_VERSIONS } from "./traceClient";
import { clinicTime } from "./traceFormat";
import { scopeVersion, useEventCatalog } from "./traceQueries";
import type { CatalogScope, CatalogUnassigned, EventCatalog } from "./types";

const CATEGORY_LABELS: Record<CatalogCategory, string> = {
  events: "Events",
  policies: "Policies",
  candidates: "Candidates",
  actions: "Actions",
};

const WINDOW_OPTIONS = [
  { value: "1", label: "Last 1 day" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

const unassignedText = (unassigned: CatalogUnassigned): string | null => {
  const parts = [
    unassigned.events > 0 && plural(unassigned.events, "event"),
    unassigned.decisions > 0 && plural(unassigned.decisions, "decision"),
    unassigned.candidate_rows > 0 &&
      plural(unassigned.candidate_rows, "candidate evaluation"),
    unassigned.action_requests > 0 &&
      plural(unassigned.action_requests, "action request"),
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(", ") : null;
};

const ScopeNote = ({
  scope,
  unassigned,
}: {
  scope: CatalogScope;
  unassigned: CatalogUnassigned;
}) => {
  const unmatched = unassignedText(unassigned);
  const all = scope.policy_version === null;
  const retained = scope.included_versions.filter(
    (version) => version !== scope.current_policy_version,
  );
  return (
    <div className="space-y-1 text-xs text-annotation">
      <p>
        Usage is this clinic&rsquo;s recorded activity from{" "}
        {clinicTime(scope.window_start, scope.clinic_timezone)} to{" "}
        {clinicTime(scope.window_end, scope.clinic_timezone)}. Event emission
        counts span all policy versions.{" "}
        {all
          ? "Each policy and candidate counts only its own version's decisions; action counts span decisions under all recorded policy versions."
          : `Decision and candidate counts are for policy ${scope.policy_version} only, and action counts span only decisions recorded under it.`}
      </p>
      <p>
        Definitions are what the registry holds for{" "}
        {all
          ? "every policy version, with a policy and candidate row per version"
          : `policy ${scope.policy_version}`}
        , listed whether or not they were ever used. No creation dates are
        recorded. Policy {scope.current_policy_version} is current and used for
        new decisions
        {retained.length > 0 &&
          `; ${retained.join(", ")} ${retained.length === 1 ? "is" : "are"} retained so ${retained.length === 1 ? "its" : "their"} records replay`}
        .
      </p>
      {unmatched && (
        <p className="text-ink-body">
          {unmatched} recorded in this scope matched no definition in{" "}
          {all
            ? "any policy version registered in this build"
            : `policy ${scope.policy_version}`}
          ; they are counted here, not attributed.
        </p>
      )}
    </div>
  );
};

interface Selection {
  id: string;
  name: string;
  match: CatalogMatch | null;
  version: string | null;
  /** Set when the selection was carried to this counterpart from another version. */
  moved: { from: string | null; scope: string } | null;
}

const selectionFor = (id: string, index: CatalogIndex): Selection => {
  const entry = index.entries.get(id);
  const label = index.labels.get(id);
  return {
    id,
    name: label ? fullLabel(label) : "The selected definition",
    match: entry ? catalogMatch(entry, index) : null,
    version: entry ? entryVersion(entry) : null,
    moved: null,
  };
};

const resolveSelection = (
  chosen: Selection | undefined,
  category: CatalogCategory,
  entries: CatalogEntry[],
  index: CatalogIndex | null,
  scope: CatalogScope | undefined,
) => {
  const kept = chosen && index?.entries.get(chosen.id)?.category === category;
  // Ids are opaque and version-specific; only a unique counterpart can carry a selection.
  const counterpartId =
    chosen && !kept && chosen.match && index
      ? counterpart(chosen.match, entries, index)
      : null;
  const next: Selection | null =
    chosen && counterpartId && index && scope
      ? {
          ...selectionFor(counterpartId, index),
          moved: { from: chosen.version, scope: scopeVersion(scope) },
        }
      : null;
  return {
    anchor: kept ? chosen.id : (counterpartId ?? entries[0]?.item.id ?? null),
    missing: chosen && !kept && !counterpartId ? chosen : null,
    carried:
      kept &&
      chosen.moved &&
      scope &&
      chosen.moved.scope === scopeVersion(scope)
        ? chosen.moved
        : null,
    next,
  };
};

const scopeText = (scope: CatalogScope) =>
  `${scopeVersionText(scope)}, last ${plural(scope.window_days, "day")}`;

export const CatalogPanel = ({ active }: { active: boolean }) => {
  const [windowDays, setWindowDays] = useState(7);
  const [policyVersion, setPolicyVersion] = useState<string | undefined>();
  const [category, setCategory] = useState<CatalogCategory>("events");
  const [selected, setSelected] = useState<
    Partial<Record<CatalogCategory, Selection>>
  >({});
  const [inspected, setInspected] = useState<string | null>(null);
  const query = useEventCatalog(active, policyVersion, windowDays);
  const fresh = query.data && !query.isPlaceholderData ? query.data : undefined;
  // Placeholder data covers only a pending scope; a failed one keeps the last good catalog on screen.
  const [lastGood, setLastGood] = useState<EventCatalog>();
  if (fresh && fresh !== lastGood) setLastGood(fresh);
  if (fresh && policyVersion === undefined)
    setPolicyVersion(scopeVersion(fresh.scope));
  const catalog = query.data ?? lastGood;
  const stale = catalog !== undefined && catalog !== fresh;
  const index = useMemo(
    () => (catalog ? indexCatalog(catalog) : null),
    [catalog],
  );
  const byCategory = useMemo(() => {
    const groups: Record<CatalogCategory, CatalogEntry[]> = {
      events: [],
      policies: [],
      candidates: [],
      actions: [],
    };
    index?.entries.forEach((entry) => groups[entry.category].push(entry));
    return groups;
  }, [index]);
  const entries = byCategory[category];

  const { anchor, missing, carried, next } = resolveSelection(
    selected[category],
    category,
    entries,
    index,
    catalog?.scope,
  );
  // Store the counterpart so a later scope switch starts from its row.
  if (next)
    setSelected((current) => ({
      ...current,
      [category]: next,
    }));
  const inspectedId =
    inspected && index?.entries.has(inspected) ? inspected : anchor;
  const inspectedEntry = inspectedId ? index?.entries.get(inspectedId) : null;
  const anchorLabel = anchor ? index?.labels.get(anchor) : undefined;
  const select = (id: string) => {
    if (!index) return;
    setSelected((current) => ({
      ...current,
      [category]: selectionFor(id, index),
    }));
    setInspected(null);
  };
  const navigate = (id: string) => {
    const target = index?.entries.get(id)?.category;
    if (!target || !index) return;
    setCategory(target);
    setSelected((current) => ({
      ...current,
      [target]: selectionFor(id, index),
    }));
    setInspected(null);
  };

  const versionOptions = catalog
    ? [
        { value: ALL_POLICY_VERSIONS, label: "All versions" },
        ...catalog.scope.policy_versions.map((version) => ({
          value: version,
          label: `${version} · ${version === catalog.scope.current_policy_version ? "current" : "retained"}`,
        })),
      ]
    : [];

  return (
    <div className="space-y-4" aria-busy={query.isFetching || undefined}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-ink">Event catalog</h2>
          <p className="mt-1 text-sm text-annotation">
            Every registered event, policy, candidate and action, with recorded
            usage in this clinic.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void query.refetch()}
          disabled={!active || query.isFetching}
        >
          {query.isFetching ? "Refreshing" : "Refresh"}
        </Button>
      </div>
      <div className="flex max-w-2xl flex-wrap items-end gap-3">
        <div className="w-64">
          <Select
            label="Policy version"
            value={policyVersion ?? null}
            placeholder="Loading versions"
            options={versionOptions}
            disabled={versionOptions.length === 0}
            onValueChange={(value) => {
              setPolicyVersion(value);
              setInspected(null);
            }}
          />
        </div>
        <div className="w-40">
          <Select
            label="Usage window"
            value={String(windowDays)}
            options={WINDOW_OPTIONS}
            onValueChange={(value) => setWindowDays(Number(value))}
          />
        </div>
      </div>
      {query.isError && (
        <PanelError
          label={catalog ? "the selected scope" : "the event catalog"}
          onRetry={() => void query.refetch()}
          className={catalog ? "min-h-28 p-4" : "min-h-48"}
        />
      )}
      {catalog && stale && (
        <p className="text-xs text-ink-body" role="status">
          {query.isError
            ? `Showing ${scopeText(catalog.scope)} from the last successful load.`
            : `Loading the selected scope; showing ${scopeText(catalog.scope)}.`}
        </p>
      )}
      {!catalog && query.isPending && (
        <SkeletonStack count={6} className="p-5" aria-busy="true" />
      )}
      {catalog && index && (
        <>
          <ScopeNote scope={catalog.scope} unassigned={catalog.unassigned} />
          <Tabs
            value={category}
            onValueChange={(value) => {
              setCategory(value as CatalogCategory);
              setInspected(null);
            }}
          >
            <TabList aria-label="Definition categories">
              {CATALOG_CATEGORIES.map((value) => (
                <Tab key={value} value={value}>
                  {CATEGORY_LABELS[value]}{" "}
                  <span className="text-annotation">
                    {byCategory[value].length}
                  </span>
                </Tab>
              ))}
            </TabList>
            {CATALOG_CATEGORIES.map((value) => (
              <TabPanel key={value} value={value} className="space-y-2 pt-3">
                {value === category && missing && (
                  <p className="text-xs text-ink-body" role="status">
                    {missing.name} has no unique match in{" "}
                    {scopeVersionText(catalog.scope)}
                    {anchorLabel
                      ? `; showing ${anchorLabel.name} instead.`
                      : "."}
                  </p>
                )}
                {value === category && carried && anchorLabel && (
                  <p className="text-xs text-ink-body" role="status">
                    Showing {fullLabel(anchorLabel)} in{" "}
                    {scopeVersionText(catalog.scope)}, the counterpart of the
                    selection from{" "}
                    {carried.from ? `policy ${carried.from}` : "the last scope"}
                    .
                  </p>
                )}
                {value === category &&
                  (entries.length > 0 ? (
                    <CatalogTable
                      category={category}
                      entries={entries}
                      index={index}
                      scope={catalog.scope}
                      anchor={anchor}
                      onSelect={select}
                    />
                  ) : (
                    <p className="p-6 text-center text-sm text-annotation">
                      No {CATEGORY_LABELS[value].toLowerCase()} are registered
                      in {scopeVersionText(catalog.scope)}.
                    </p>
                  ))}
              </TabPanel>
            ))}
          </Tabs>
          {anchor && anchorLabel && (
            <section
              aria-label="Relationship map"
              className="space-y-3 border-t border-rule pt-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-display text-lg text-ink">
                  Relationships · {anchorLabel.name}
                  {labelContext(anchorLabel) && (
                    <span className="ml-2 font-body text-xs text-annotation">
                      {labelContext(anchorLabel)}
                    </span>
                  )}
                </h3>
                <p className="text-xs text-annotation">
                  Select a node to highlight its path and inspect it below. All
                  branches remain visible and the table selection stays put.
                </p>
              </div>
              <RelationshipMap
                key={`${scopeVersion(catalog.scope)} ${anchor}`}
                anchor={anchor}
                index={index}
                scope={scopeVersionText(catalog.scope)}
                inspected={inspectedId}
                onInspect={setInspected}
              />
            </section>
          )}
          {inspectedEntry && (
            <CatalogInspector
              key={inspectedEntry.item.id}
              entry={inspectedEntry}
              index={index}
              scope={catalog.scope}
              isAnchor={inspectedEntry.item.id === anchor}
              onInspect={setInspected}
              onNavigate={() => navigate(inspectedEntry.item.id)}
            />
          )}
        </>
      )}
    </div>
  );
};
